/**
 * ManageSchemasDialog Component
 *
 * Dialog for managing schemas within a Schema Group.
 * Allows adding/removing schemas and configuring retention policies.
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Database, Trash2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  groupApi,
  SchemaGroup,
  SchemaGroupMembership,
  Schema,
  RETENTION_PRESETS,
} from '@/services/api/groupApi';
import { serverApi, DatabaseConnection } from '@/services/api/serverApi';
import * as errorUtils from '@/utils/errors';

interface ManageSchemasDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group: SchemaGroup | null;
  onSuccess: () => void;
}

export const ManageSchemasDialog: React.FC<ManageSchemasDialogProps> = ({
  open,
  onOpenChange,
  group,
  onSuccess,
}) => {
  // State
  const [memberships, setMemberships] = useState<SchemaGroupMembership[]>([]);
  const [availableSchemas, setAvailableSchemas] = useState<Schema[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add schema form
  const [selectedSchemaId, setSelectedSchemaId] = useState<string>('');
  const [selectedRetention, setSelectedRetention] = useState<number>(90);
  const [isAdding, setIsAdding] = useState(false);

  // Database connections
  const [databaseConnections, setDatabaseConnections] = useState<DatabaseConnection[]>([]);

  // Fetch data when dialog opens
  useEffect(() => {
    if (open && group) {
      fetchData();
    }
  }, [open, group]);

  const fetchData = useCallback(async () => {
    if (!group) return;

    setIsLoading(true);
    setError(null);

    try {
      // Fetch memberships (schemas in this group)
      const groupMemberships = await groupApi.getGroupSchemas(group.id, true);
      setMemberships(groupMemberships);

      // Fetch all available schemas (draft and published).
      // Use a high limit because platform admins see schemas across all orgs;
      // the backend default of 20/page would hide schemas later in the alphabet.
      const schemas = await groupApi.listSchemas({ limit: 500 });
      setAvailableSchemas(schemas);

      // Fetch available database connections
      try {
        const connections = await serverApi.listConnections();
        setDatabaseConnections(connections);
      } catch {
        // Non-critical - continue with empty list (will use default)
        setDatabaseConnections([]);
      }
    } catch (err: unknown) {
      const errorMessage =
        errorUtils.getErrorMessageOrFallback(err, 'Failed to load schemas');
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [group]);

  // Get schemas not already in the group
  const schemasNotInGroup = availableSchemas.filter(
    (schema) => !memberships.some((m) => m.schemaId === schema.id)
  );

  // Handle adding a schema to the group
  const handleAddSchema = useCallback(async () => {
    if (!group || !selectedSchemaId) return;

    setIsAdding(true);
    setError(null);

    try {
      await groupApi.addSchemaToGroup(group.id, {
        schemaId: selectedSchemaId,
        storageConfig: {
          primary: 'mongo',
          retentionDays: selectedRetention,
        },
      });

      // Refresh memberships
      await fetchData();
      setSelectedSchemaId('');
      setSelectedRetention(90);
      onSuccess();
    } catch (err: unknown) {
      const errorMessage =
        errorUtils.getErrorMessageOrFallback(err, 'Failed to add schema');
      setError(errorMessage);
    } finally {
      setIsAdding(false);
    }
  }, [group, selectedSchemaId, selectedRetention, fetchData, onSuccess]);

  // Handle removing a schema from the group
  const handleRemoveSchema = useCallback(
    async (schemaId: string) => {
      if (!group) return;

      if (!window.confirm('Are you sure you want to remove this schema from the group?')) {
        return;
      }

      try {
        await groupApi.removeSchemaFromGroup(group.id, schemaId);
        await fetchData();
        onSuccess();
      } catch (err: unknown) {
        const errorMessage =
          errorUtils.getErrorMessageOrFallback(err, 'Failed to remove schema');
        setError(errorMessage);
      }
    },
    [group, fetchData, onSuccess]
  );

  // Handle updating retention
  const handleUpdateRetention = useCallback(
    async (schemaId: string, retentionDays: number) => {
      if (!group) return;

      try {
        await groupApi.updateMembership(group.id, schemaId, {
          storageConfig: {
            retentionDays,
          },
        });
        await fetchData();
        onSuccess();
      } catch (err: unknown) {
        const errorMessage =
          errorUtils.getErrorMessageOrFallback(err, 'Failed to update retention');
        setError(errorMessage);
      }
    },
    [group, fetchData, onSuccess]
  );

  // Get retention label
  const getRetentionLabel = (days: number | undefined): string => {
    if (days === undefined) return 'Default';
    const preset = RETENTION_PRESETS.find((p) => p.value === days);
    return preset ? preset.label : `${days} days`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Manage Schemas
            {group && (
              <span className="text-sm font-normal text-muted-foreground ml-2">
                in {group.name}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Group Database Info */}
          {group && (
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm flex items-center gap-2">
              <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
              </svg>
              <span className="text-blue-800 dark:text-blue-300">
                <strong>Database:</strong>{' '}
                {group.databaseConnectionId
                  ? databaseConnections.find(d => d.id === group.databaseConnectionId)?.name || 'Custom Connection'
                  : 'Default Connection'}
              </span>
            </div>
          )}

          {/* Add Schema Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Add Schema to Group</h3>

            <div className="flex gap-4">
              {/* Schema Selector */}
              <div className="flex-1 space-y-2">
                <Label>Schema</Label>
                <Select value={selectedSchemaId} onValueChange={setSelectedSchemaId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a schema..." />
                  </SelectTrigger>
                  <SelectContent>
                    {schemasNotInGroup.length === 0 ? (
                      <div className="p-2 text-sm text-muted-foreground">
                        No schemas available to add
                      </div>
                    ) : (
                      schemasNotInGroup.map((schema) => (
                        <SelectItem key={schema.id} value={schema.id}>
                          {schema.displayName || schema.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Retention Selector */}
              <div className="w-32 space-y-2">
                <Label>Retention</Label>
                <Select
                  value={String(selectedRetention)}
                  onValueChange={(v) => setSelectedRetention(Number(v))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RETENTION_PRESETS.map((preset) => (
                      <SelectItem key={preset.value} value={String(preset.value)}>
                        {preset.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Add Button */}
              <div className="self-end">
                <Button
                  onClick={handleAddSchema}
                  disabled={!selectedSchemaId || isAdding}
                >
                  {isAdding ? 'Adding...' : 'Add'}
                </Button>
              </div>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 rounded-lg bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Current Schemas Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">
              Schemas in Group ({memberships.length})
            </h3>

            {isLoading ? (
              <div className="space-y-2 py-2">
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
              </div>
            ) : memberships.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Database className="h-10 w-10 text-muted-foreground mb-2 opacity-50" />
                <p className="text-muted-foreground">No schemas in this group yet.</p>
                <p className="text-sm text-muted-foreground">Add one above to get started.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {memberships.map((membership) => (
                  <div
                    key={membership.id}
                    className="flex items-center justify-between p-3 border border-border rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">
                        {membership.schema?.displayName || membership.schema?.name || membership.schemaId}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {membership.collectionName}
                      </div>
                    </div>

                    {/* Retention Selector */}
                    <div className="w-32 mx-4">
                      <Select
                        value={String(membership.storageConfig?.retentionDays ?? 90)}
                        onValueChange={(v) =>
                          handleUpdateRetention(membership.schemaId, Number(v))
                        }
                      >
                        <SelectTrigger className="h-8 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {RETENTION_PRESETS.map((preset) => (
                            <SelectItem key={preset.value} value={String(preset.value)}>
                              {preset.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Status Badge */}
                    <Badge
                      variant={membership.status === 'active' ? 'success' : 'warning'}
                      className="mx-2"
                    >
                      {membership.status}
                    </Badge>

                    {/* Remove Button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveSchema(membership.schemaId)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
            <strong>Note:</strong> All schemas in this group share the same database connection.
            Each schema has its own collection. Retention policies control how long data is kept.
            Set to "Forever" (0 days) to keep data indefinitely.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ManageSchemasDialog;
